import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { APP_NAME } from "@shared/constants";
import { AuthProvider } from "@client/components/AuthProvider";
import { useAuth } from "@client/hooks/useAuth";
import { queryClient } from "@client/lib/queryClient";
import { router } from "@client/router";

export function App() {
  return (
    // Query outside Auth: `AuthProvider` clears the cache when a session ends,
    // so the client has to exist before it does.
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RoutedApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RoutedApp() {
  const auth = useAuth();

  // The route guards read `context.auth`, which only updates when the router is
  // told to look again. Without this, signing out would leave the last guarded
  // page on screen until the next navigation.
  //
  // Not while the boot call is still in flight: the router is not mounted yet,
  // so its context still holds the placeholder and a guard would read a session
  // that does not exist. Once `RouterProvider` renders, its own effect fills the
  // context in before this one runs — child effects go first.
  useEffect(() => {
    if (auth.status === "loading") return;
    void router.invalidate();
  }, [auth.status]);

  // Nothing is rendered until the boot `me` call has settled. A login form that
  // flashes past for an already-signed-in person reads as a session that was lost.
  if (auth.status === "loading") return <Splash />;

  return <RouterProvider router={router} context={{ auth }} />;
}

function Splash() {
  return (
    <main className="flex min-h-screen items-center justify-center px-4" aria-busy="true">
      <p className="text-sm font-semibold text-ink-soft">{APP_NAME}</p>
    </main>
  );
}
